/**
 * @fileoverview Shared vanilla-cookieconsent config and runner for Korvo (shell + static file homepage).
 *
 * @purpose Single source for CMP copy, cookie name, and `run()` wiring per docs/COOKIE_CONSENT.md.
 * @usage Shell `CookieConsentClient` imports `runKorvoVanillaCookieConsent`; `file-homepage-boot.ts` bundles for IIFE.
 * @ownership @korvo/core
 *
 * @docs docs/COOKIE_CONSENT.md
 */

const COOKIE_CONSENT_NAME = 'korvo_cookie_consent';
const COOKIE_CONSENT_EXPIRY_DAYS = 365;

function injectOpaqueStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('korvo-cookieconsent-override')) return;
  const style = document.createElement('style');
  style.id = 'korvo-cookieconsent-override';
  style.textContent =
    '#cc-main .cm { background: var(--background-secondary) !important; } ' +
    '#cc-main { background: var(--background-secondary) !important; }';
  document.head.appendChild(style);
}

export type KorvoVanillaCookieConsentConfig = {
  cookie: { name: string; expiresAfterDays: number };
  categories: Record<string, { readOnly?: boolean; enabled?: boolean }>;
  guiOptions: {
    consentModal: {
      layout: 'box';
      position: 'bottom right';
      equalWeightButtons: boolean;
      flipButtons: boolean;
    };
  };
  language: {
    default: string;
    translations: Record<
      string,
      {
        consentModal: {
          title: string;
          description: string;
          acceptAllBtn: string;
          acceptNecessaryBtn: string;
        };
        preferencesModal: {
          title: string;
          acceptAllBtn: string;
          acceptNecessaryBtn: string;
          savePreferencesBtn: string;
          sections: unknown[];
        };
      }
    >;
  };
  onModalReady: (opts: { modalName: string }) => void;
  /** When true (library default), consent UI is skipped for `navigator.webdriver` (Playwright). Must be false for E2E. */
  hideFromBots?: boolean;
};

/**
 * Korvo CMP config for vanilla-cookieconsent v3 (German default UI).
 */
export function buildKorvoVanillaCookieConsentConfig(): KorvoVanillaCookieConsentConfig {
  return {
    hideFromBots: false,
    cookie: {
      name: COOKIE_CONSENT_NAME,
      expiresAfterDays: COOKIE_CONSENT_EXPIRY_DAYS,
    },
    categories: {
      necessary: { readOnly: true, enabled: true },
    },
    guiOptions: {
      consentModal: {
        layout: 'box',
        position: 'bottom right',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },
    language: {
      default: 'de',
      translations: {
        de: {
          consentModal: {
            title: 'Cookie-Einstellungen',
            description:
              'Wir verwenden Cookies, um Ihre Sitzung zu verwalten und die Funktionalität der Website zu gewährleisten. ' +
              'Durch Klicken auf "Akzeptieren" stimmen Sie der Verwendung von Cookies zu. Weitere Informationen finden Sie in unserer ' +
              '<a href="/privacy">Datenschutzerklärung</a>.',
            acceptAllBtn: 'Akzeptieren',
            acceptNecessaryBtn: 'Ablehnen',
          },
          preferencesModal: {
            title: '',
            acceptAllBtn: '',
            acceptNecessaryBtn: '',
            savePreferencesBtn: '',
            sections: [],
          },
        },
      },
    },
    onModalReady: ({ modalName }: { modalName: string }) => {
      if (modalName === 'consentModal') injectOpaqueStyle();
    },
  };
}

type CookieConsentRun = (config: KorvoVanillaCookieConsentConfig) => Promise<void>;

/** Dynamic import namespace for `vanilla-cookieconsent` (no `import()` in type position — ESLint consistent-type-imports). */
type VanillaCookieConsentEntry = {
  run?: CookieConsentRun
}

type VanillaCookieConsentModule = VanillaCookieConsentEntry & {
  default?: VanillaCookieConsentEntry
}

/**
 * Dynamically loads vanilla-cookieconsent and runs Korvo config (safe for client components and browser bundle).
 */
export async function runKorvoVanillaCookieConsent(): Promise<void> {
  const mod = (await import('vanilla-cookieconsent')) as VanillaCookieConsentModule
  const lib = mod.default ?? mod
  if (typeof lib.run !== 'function') return
  const config = buildKorvoVanillaCookieConsentConfig()
  injectOpaqueStyle()
  await lib.run(config)
}
